/* eslint-disable @typescript-eslint/no-unused-vars */
import { Session } from 'next-auth';

import {
    ImpactSimulationRequestOptions,
    ImpactSimulationResponse,
    OffresSecondaire,
    OpApplicable,
    RecapitulatifPU,
} from '@app-types/api/mcube';
import {
    ACTION_TYPES,
    Advantage,
    ApplicableOp,
    Contract,
    ImpactsSimulation,
    SecondaryOffer,
    SummaryPU,
} from '@app-types/simulator/simulator';
import { UserTypeEnum } from '@app-types/user';

import { IMPACT_PU_MOCK } from 'src/app/(promo-simulator)/_mocks/impactPu';

import { getAppConfig } from './config';
import { apiHttpService } from './http';

const appConfig = getAppConfig();

export const getImpactSimulation = async (session: Session, gencodes: string[]): Promise<ImpactsSimulation | null> => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.user?.accessToken}`,
        'x-version': '2',
        'x-process': 'SimulerImpactPU',
    };

    if (session?.user?.user_type === UserTypeEnum.CDC) {
        return null;
    }

    const data: ImpactSimulationRequestOptions = {
        idOffreCible: gencodes.filter((g) => g),
        parcours: 'ACTIVATION',
    };

    const response = await apiHttpService.post<ImpactSimulationResponse>(
        `${appConfig.sapi.url}/personnes/${session?.user?.sub}/simulation-impacts-parc`,
        data,
        { headers },
    );

    if (appConfig.mocks.mcube) {
        return mapToSimulationType(IMPACT_PU_MOCK);
    }
    return mapToSimulationType(response);
};

const mapToSimulationType = (apiData: ImpactSimulationResponse): ImpactsSimulation => {
    return {
        contracts: apiData.contrats?.map((contract) => ({
            id: contract.idContrat,
            msisdn: contract.msisdn,
            voipNumber: contract.numeroVoip,
            applicableOp: mapApplicableOp(contract.opApplicable),
            secondaryOffers: contract.offresSecondaires?.map((offer) => mapSecondaryOffer(offer)),
        })),
        currentCommercialOrders: apiData?.commandesCommercialesEnCours?.map((order) => ({
            mainOffers: order.offresPrincipales.map((el) => mapApplicableOp(el)),
            orderId: order.idCommande,
        })),
        messages: apiData?.messages?.map((message) => ({
            code: message.code,
            label: message.libelle,
            parameters: message?.parametres?.map((param) => ({
                code: param.code,
                value: param.valeur,
            })),
        })),
        proposed: apiData?.proposees?.map((offer) => ({
            secondaryOffers: offer.offresSecondaires?.map((offer) => mapSecondaryOffer(offer)),
            journey: offer.parcours,
            applicableOp: mapApplicableOp(offer.opApplicable),
        })),
        summaryPU: mapToSummary(apiData.recapitulatifPU),
    };
};

const mapToSummary = (recapitulatifPU: RecapitulatifPU): SummaryPU => {
    return {
        currentPlan: {
            mainOffers: {
                globalAmount: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobal,
                globalAmountCurrentOrders:
                    recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalCommandesEncours,
                globalAmountHeldOffers: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalOffresDetenues,
                globalAmountProposed: recapitulatifPU.forfaitActuel.offresPrincipales.montantGlobalPropose,
            },
            toutatisDiscounts: {
                globalAmount: recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobal,
                globalAmountCurrentOrders:
                    recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPourCommandesEncours,
                globalAmountHeldOffers: recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPourOffresDetenues,
                globalAmountProposed: recapitulatifPU.forfaitActuel.remisesToutatis.montantGlobalPropose,
            },
        },
        simulationImpact: {
            mainOffers: {
                globalAmount: recapitulatifPU.impactSimulation.offresPrincipales.montantGlobal,
                globalAmountCurrentOrders:
                    recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalCommandesEncours,
                globalAmountHeldOffers: recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalOffresDetenues,
                globalAmountProposed: recapitulatifPU.impactSimulation.offresPrincipales.montantGlobalPropose,
            },
            toutatisDiscounts: {
                globalAmount: recapitulatifPU.impactSimulation.remisesToutatis.montantGlobal,
                globalAmountCurrentOrders:
                    recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPourCommandesEncours,
                globalAmountHeldOffers:
                    recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPourOffresDetenues,
                globalAmountProposed: recapitulatifPU.impactSimulation.remisesToutatis.montantGlobalPropose,
            },
        },
    };
};

const mapSecondaryOffer = (apiData: OffresSecondaire): SecondaryOffer => {
    return {
        action: apiData.action,
        offerId: apiData.idOffre,
        commercialLabel: apiData.libelleCommercial,
        priceExcludingTax: apiData.tarifHT,
        priceIncludingTax: apiData.tarifTTC,
        vatRate: apiData.tauxTVA,
        incompatibleType: apiData.typeIncompatible,
        offerType: apiData.typeOffre,
    };
};

const mapApplicableOp = (apiData: OpApplicable): ApplicableOp => {
    return {
        advantages: apiData.avantages?.map((advantage) => ({
            action: advantage.action as ACTION_TYPES,
            discountIncludingTax: advantage.remiseTTC,
            duration: advantage.duree,
            durationUnit: advantage.uniteDuree,
            label: advantage.libelle,
            endOfValidityDate: advantage.dateFinValidite,
            familyPack: advantage.packFamille,
            offerId: advantage.idOffre,
            vatRate: advantage.tauxTVA,
        })),
        compatibleWithDiscounts: apiData.compatibleAvecRemises,
        conditionForDiscounts: apiData.conditionPourRemises,
        endOfCommitmentDate: apiData.dateFinEngagement,
        commitmentDuration: apiData.dureeEngagement,
        convergenceRange: apiData.gammeConvergence,
        offerId: apiData.idOffre,
        commercialLabel: apiData.libelleCommercial,
        marketLine: apiData.ligneMarche,
        convergentMarketLine: apiData.ligneMarcheConvergent,
        amountPaid: apiData.montantPaye,
        amountPaidAfterSimulation: apiData.montantPayeApresSimulation,
        convergenceRangeLevel: apiData.niveauGammeConvergent,
        options: apiData.options?.map((option) => ({
            label: option.libelle,
            amountIncludingTax: option.montantTTC,
            vatRate: option.tauxTVA,
        })),
        priceIncludingTax: apiData.tarifTTC,
    };
};

export function getFamilyContract(simulation: ImpactsSimulation | undefined): Contract[] {
    if (!simulation) {
        return [];
    }

    const contractsForFamily =
        simulation?.contracts?.filter((c) => c.applicableOp.advantages?.some((a) => familyAdvantagesPredicate(a))) ||
        [];
    const currentOrderFamily = simulation?.currentCommercialOrders?.filter((order) =>
        order.mainOffers.some((offer) => offer.advantages?.some((a) => familyAdvantagesPredicate(a))),
    );
    const currentOrderToContract: Contract[] =
        currentOrderFamily?.flatMap((order) =>
            order.mainOffers?.map((offer) => ({ id: order.orderId, applicableOp: offer })),
        ) || [];

    return [...contractsForFamily, ...currentOrderToContract];
}

export function familyAdvantagesPredicate(advantage: Advantage): boolean {
    return (
        advantage.familyPack &&
        (advantage.action === ACTION_TYPES.CONSERVATION || advantage.action === ACTION_TYPES.SOUSCRIPTION)
    );
}
